import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_03 – obstacle straight "aktiv" 20 m (design/01_segments/SEG_03_Obstacle_Straight).
 * Deliberately mixed rows: every variant contains a slide and a lane change next to
 * jumps, so the player has to read and combine actions. Bottle lines run through the
 * lanes as usual – over jumps, under slides, right up to the lane closures (risky).
 *
 *   Row A z=5, row B z=15 (10 m apart → one lane change fits at top speed)
 *   V1  A: L0 slide · L1 closure       B: L1 jump · L2 container
 *   V3  A: L1 jump  · L2 slide         B: L0 container · L1 slide
 *   V2 / V4 = mirrored
 * The containers' lanes stay free before them – their roofs are a double-jump route (level/terrain/roofRunUp.ts:
 * 13.7 m of plain road before the face, the runner back on the road after the obstacle before; V3 had its jump
 * in the container lane until 2026-10-02).
 *
 * The sheet's variant with a slow truck is SEG_OBSTACLE_TRUCK_40M (same family, moving hazard).
 */
const LENGTH = WORLD_CONFIG.baseSegmentLength;
const ROW_A = 5;
const ROW_B = 15;
const ACTIVE_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;

const place = (row: 'A' | 'B', lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

/** Left/right mirror of a layout (lane l ↔ lane 2 − l). */
function mirrored(id: string, variant: ObstacleVariant): ObstacleVariant {
  const lastLane = WORLD_CONFIG.laneCount - 1;
  return {
    id,
    weight: variant.weight,
    placements: variant.placements.map((p) => ({
      ...p,
      spawnPointId: p.spawnPointId.replace(/_L(\d)$/, (_, lane: string) => `_L${lastLane - Number(lane)}`),
    })),
  };
}

const V1: ObstacleVariant = {
  id: 'V1_SLIDE_CLOSURE',
  weight: 1,
  placements: [
    place('A', 0, 'OBS_SCAFFOLD_PASS_A'),
    place('A', 1, 'OBS_LANE_CLOSURE_A'),
    place('B', 1, 'OBS_BARRIER_BOARD_A'),
    place('B', 2, 'OBS_CONTAINER_A'),
  ],
};

const V3: ObstacleVariant = {
  id: 'V3_JUMP_SLIDE_CONTAINER',
  weight: 1,
  placements: [
    place('A', 1, 'OBS_PALLET_A'),
    place('A', 2, 'OBS_SCAFFOLD_PASS_A'),
    place('B', 0, 'OBS_CONTAINER_A'),
    place('B', 1, 'OBS_SCAFFOLD_PASS_A'),
  ],
};

const SIDES = moduleSides(LENGTH);

export const SEG_OBSTACLE_20M: SegmentDefinition = {
  id: 'SEG_OBSTACLE_20M',
  kind: SegmentKind.Straight,
  displayName: 'Hindernisgerade 20 m',
  length: LENGTH,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',

  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(WORLD_CONFIG),

  obstacleSpawnPoints: [...obstacleRow(WORLD_CONFIG, 'A', ROW_A, ACTIVE_TYPES), ...obstacleRow(WORLD_CONFIG, 'B', ROW_B, ACTIVE_TYPES)],
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [V1, mirrored('V2_SLIDE_CLOSURE_MIRRORED', V1), V3, mirrored('V4_JUMP_SLIDE_CONTAINER_MIRRORED', V3)],

  collectibleSpawnPoints: collectibleLanes(WORLD_CONFIG, 1, LENGTH - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  ...SIDES,

  generation: {
    weight: 0.45,
    minDistance: 100,
    family: 'OBSTACLE_20',
    tags: ['straight', 'obstacles', 'mixed'],
  },
};
